// /2026-start paywall — paid trial vs free trial, paid-UA traffic only.
//
// Arms:
//   control    Current /2026 offering "Web: Yearly, Monthly, Weekly with 3dayTrial"
//              (Weekly / Monthly / Yearly, all with a 3-day free trial).
//   treatment  Offering "Web: Monthly 3dayTrial, Yearly 7day $0.99 paid trial":
//                - Monthly  $19.99/month, 3-day free trial
//                - Yearly   $99.99/year,  7-day paid trial at $0.99 (RC intro price)
//              Yearly is preselected + MOST POPULAR (visual-middle of 2 cards).
//
// Hypothesis:
//   A $0.99 paid trial filters out low-intent trialists, so fewer trials start
//   but more of them convert — and a full week gives the habit time to form
//   before the $99.99 charge. Net: higher paid conversions (and revenue) per
//   paywall viewer from paid UA, even if raw trial starts fall.
//
// Primary metric (analyze in Amplitude, group by the experiment):
//   Paid conversions per exposed user — `Web Funnel - Trial Converted` +
//   `Web Funnel - Subscription Started`, with revenue. Needs ≥7 days after the
//   last exposure before the yearly arm's conversions land.
//
// Note: treatment also changes the paywall design (no countdown banner or
// trial badge, muted sage palette), so a win measures "paid trial + new
// design" together, not pricing alone.
//
// Secondary / guardrails:
//   - Paywall Reached → Checkout Completed   (upfront friction of paying $0.99)
//   - Plan mix (Plan Selected / Checkout Completed by package_identifier)
//   - Trial→paid conversion rate per arm
//   - Refund rate (30-day money-back guarantee still applies)
//
// Targeting — enforced at render time (local-eval rejects user-property
// segments; see start_polish_v1 for the same constraint):
//   /2026-start AND paid-UA session (ad click ID or paid utm_medium on the
//   landing URL — see isPaidUaSession() in src/paid-trial.js). Exposure fires
//   from getTargetOffering() when an eligible user reaches the s49 paywall, so
//   the denominator is "paid-UA users who saw the paywall". Organic users and
//   every other funnel never call getVariant() for this flag.
//
// PREREQUISITE — RevenueCat (manual, before flipping Running):
//   Create offering with identifier exactly
//   "Web: Monthly 3dayTrial, Yearly 7day $0.99 paid trial" containing:
//     $rc_monthly  → Web Billing product $19.99/month, 3-day free trial
//     $rc_annual   → Web Billing product $99.99/year, intro price $0.99 for 7 days (1 week)
//   If the offering is missing, treatment users silently fall back to the
//   control offering (a warning is logged and `offering_identifier` on
//   `Paywall Viewed` shows it) — check that field before reading results.
//
// QA:
//   ?exp_override=paid_trial_v1:treatment  (non-prod only; also needs a paid
//   signal on the URL, e.g. &utm_medium=paid_social or &fbclid=test)
//
// Rollout plan:
//   Stage 0: Attach the "Opal Web Funnel" deployment in the Amplitude UI.
//   Stage 1: Running 50/50 at 10% rollout — verify exposure fires only on paid
//            sessions, offering_identifier is correct per arm, $0.99 charge shows
//            in Stripe for the yearly arm.
//   Stage 2: 100% rollout. Read after ~1,000 paywall viewers per arm + 7 days.

import { defineExperiment } from '../src/lib/experiments/define';

export default defineExperiment({
  key: 'paid_trial_v1',
  name: '/2026-start paywall — paid trial (paid UA)',
  description:
    'Paid-UA traffic on /2026-start only. Control: current 3-day free-trial offering (Weekly/Monthly/Yearly). Treatment: Monthly $19.99 with 3-day free trial + Yearly $99.99 with a 7-day $0.99 paid trial. Exposure fires at the s49 paywall. Primary metric is paid conversions per exposed user.',
  evaluationMode: 'local',
  bucketingKey: 'amplitude_id',
  bucketingUnit: 'User',
  variants: [
    { key: 'control', description: 'Current offering: Weekly / Monthly / Yearly, 3-day free trial.' },
    { key: 'treatment', description: 'Monthly $19.99 (3-day free trial) + Yearly $99.99 (7-day $0.99 paid trial).' },
  ],
  rolloutWeights: { control: 1, treatment: 1 }, // 50/50
  // No mutualExclusionGroup: mutex groups aren't available on Opal's Amplitude
  // plan. Don't launch another /2026-start paywall test while this one runs.
  // targetSegments intentionally omitted — local-eval rejects user-property
  // segment shapes. Scoping is enforced at render time via isPaidTrialEligible().
  experimentType: 'a-b-test',
  tags: ['2026-start', 'paywall', 'pricing', 'paid-trial', 'paid-ua'],
  primaryMetric: 'paid_conversions_per_exposed_user',
});
